import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MarketingMarketSelect from './MarketingMarketSelect';

const SearchModal = ({
  isOpen,
  onClose,
  searchQuery,
  setSearchQuery,
  marketCode,
  setMarketCode,
  marketOptions = [],
  marketsLoading = false,
}) => {
  const [focusedInput, setFocusedInput] = React.useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const selectedMarketLabel =
    marketOptions.find((o) => o.value === marketCode)?.label || '';

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.append('q', searchQuery);
    if (marketCode) {
      params.append('country', marketCode);
      if (selectedMarketLabel) params.append('location', selectedMarketLabel);
    }
    onClose();
    navigate(`/search?${params.toString()}`);
  };

  const handleClear = () => {
    setSearchQuery('');
    setMarketCode('');
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="theme-marketing marketing-app fixed inset-0 z-[99999] overflow-hidden"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      <div className="pointer-events-none absolute inset-0 flex items-end justify-center md:items-center md:p-4">
        <div
          className="pointer-events-auto flex h-full w-full flex-col bg-white text-m-ink shadow-xl md:h-auto md:max-h-[85vh] md:max-w-lg md:rounded-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{ animation: 'slideUp 0.3s ease-out' }}
        >
          <div className="flex shrink-0 justify-center pb-2 pt-3 md:hidden">
            <div className="h-1.5 w-12 rounded-full bg-m-border-light" />
          </div>

          <div className="flex shrink-0 items-start justify-between px-5 pb-4 pt-2 sm:px-6 md:px-8 md:pt-8">
            <div className="flex-1 pr-4">
              <h3 className="mb-1 text-xl font-medium tracking-tight text-m-ink">
                Search vendors
              </h3>
              <p className="text-sm text-m-ink-muted">
                Find the right provider for your event
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-1 shrink-0 rounded-full p-2 transition-colors hover:bg-m-canvas-light"
              aria-label="Close search"
            >
              <X className="h-5 w-5 text-m-ink-muted" />
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto px-5 pb-4 sm:px-6 md:px-8">
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.06em] text-m-ink-muted">
                What do you need?
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2">
                  <Search
                    className={`h-4 w-4 ${
                      focusedInput === 'search' ? 'text-m-ink' : 'text-m-ink-faint'
                    }`}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Photo booth, DJ, caterer…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setFocusedInput('search')}
                  onBlur={() => setFocusedInput(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  className={`w-full rounded-xl border bg-m-canvas-light py-3 pl-11 pr-3 text-sm font-medium text-m-ink placeholder:text-m-ink-faint focus:outline-none ${
                    focusedInput === 'search'
                      ? 'border-m-ink'
                      : 'border-m-border-light'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.06em] text-m-ink-muted">
                Where?
              </label>
              <MarketingMarketSelect
                value={marketCode}
                onChange={setMarketCode}
                options={marketOptions}
                loading={marketsLoading}
                variant="field"
              />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3 border-t border-m-border-light bg-m-canvas-light px-5 py-4 sm:px-6 md:rounded-b-2xl md:px-8">
            <button
              type="button"
              onClick={handleClear}
              className="rounded-xl border border-m-border-light px-4 py-3 text-sm font-medium text-m-ink-muted transition-colors hover:border-m-ink hover:text-m-ink"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleSearch}
              className="marketing-btn marketing-btn-ink flex flex-1 items-center justify-center gap-2"
            >
              <Search className="h-4 w-4" />
              Search
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SearchModal;
